'use client';

import { useCallback, useEffect, useState } from 'react';

import debounce from 'debounce';

interface UseBottomBarVisibilityOptions {
  triggerSelector: string;
  debounceMs?: number;
  observerOptions?: IntersectionObserverInit;
}

/**
 * Хук для управления видимостью BottomBar на основе видимости триггер-элемента
 *
 * @param options - настройки хука
 * @returns объект с состоянием видимости BottomBar
 */
export const useBottomBarVisibility = ({
  triggerSelector,
  debounceMs = 150,
  observerOptions = {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px',
  },
}: UseBottomBarVisibilityOptions) => {
  const [shouldShow, setShouldShow] = useState(false);
  const [debouncedShow, setDebouncedShow] = useState(false);

  const debouncedSetShow = useCallback(
    debounce(() => {
      setDebouncedShow(shouldShow);
    }, debounceMs),
    [shouldShow, debounceMs],
  );

  useEffect(() => {
    const triggerElement = document.querySelector(triggerSelector);

    if (!triggerElement) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      const newShouldShow = !entry.isIntersecting;
      setShouldShow(newShouldShow);

      if (newShouldShow) {
        setDebouncedShow(true);
      } else {
        debouncedSetShow();
      }
    }, observerOptions);

    observer.observe(triggerElement);

    return () => {
      observer.unobserve(triggerElement);
    };
  }, [triggerSelector, debouncedSetShow, observerOptions]);

  return {
    shouldShowBottomBar: debouncedShow,
    isTriggerVisible: !shouldShow,
  };
};
